import { useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useUploadStore } from "../../store/uploadStore";
import type { User } from "../../types";
import { skipsFileConversionJobMonitoring } from "../../utils/uploadStopBehavior";

interface ActiveJobInfo {
  type: "conversion";
  jobId: string;
  pollingTimeoutId?: NodeJS.Timeout;
}

interface UseActiveItemsMonitoringProps {
  activeJobsRef: React.MutableRefObject<Map<string, ActiveJobInfo>>;
  monitorConversion: (
    id: string,
    jobId: string,
    storagePath: string,
    originalFilename: string
  ) => Promise<void>;
}

/**
 * 활성 아이템 모니터링 로직을 처리하는 훅
 */
export const useActiveItemsMonitoring = ({
  activeJobsRef,
  monitorConversion,
}: UseActiveItemsMonitoringProps) => {
  const { user } = useAuth();
  const userToken = (user as User)?.token;
  const items = useUploadStore((s) => s.items);

  useEffect(() => {
    const itemsArray = Object.values(items);

    // 활성 아이템만 필터링
    const activeItems = itemsArray.filter((i) =>
      [
        "uploading",
        "uploaded",
        "converting",
        "optimizing",
        "bundling",
        "processing",
        "rendering",
        "finalizing",
        "downloading",
      ].includes(i.status)
    );

    // token이 없으면 SSE 모니터링은 시작하지 않음
    if (!userToken) {
      return;
    }

    activeItems.forEach((item) => {
      // URL allocation already knows the server Job, but it is not a submitted
      // conversion. Unknown acceptance is reconciled before normal monitoring.
      if (item.editorImportAdmission && item.editorImportAdmission.remotePhase !== "accepted") return;
      if (skipsFileConversionJobMonitoring(item.flow)) {
        return;
      }

      // If item is in a converter-owned stage but not being monitored
      if (
        (item.status === "converting" ||
          item.status === "optimizing" ||
          (item.flow === "editor_object_import" &&
            (item.status === "uploading" || item.status === "uploaded"))) &&
        item.jobId &&
        !activeJobsRef.current.has(item.id)
      ) {
        monitorConversion(
          item.id,
          item.jobId,
          item.storagePath || "",
          item.fileName
        );
      }
    });
  }, [items, userToken, monitorConversion, activeJobsRef]);
};
